// CustomerDashboard.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Bar } from "react-chartjs-2";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    ArcElement,
    Tooltip,
    Legend,
} from "chart.js";

import Autocomplete from "@mui/material/Autocomplete";
import Checkbox from "@mui/material/Checkbox";
import {
    TextField,
    CircularProgress,
    Tooltip as MuiTooltip,
    Card,
    CardContent,
    Chip,
    Button,
    Box,
    Grid,
    Divider,
    Stack,
} from "@mui/material";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import CrudService from "services/cruds-service";
import DataTable from "examples/Tables/DataTable";
import { useNavigate } from "react-router-dom";

import { DatePicker } from "@mui/x-date-pickers";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, Tooltip, Legend);

// -------------------- FX --------------------
const fetchExchangeRates = async () => {
    try {
        const res = await fetch("https://api.exchangerate-api.com/v4/latest/USD");
        return await res.json();
    } catch (error) {
        console.error("API Error:", error);
        return null;
    }
};

const fmt2 = (n) =>
    Number(n || 0).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });

function formatDateDDMMYYYY(dateString) {
    if (!dateString) return "";
    const d = new Date(dateString);
    if (isNaN(d.getTime())) return dateString;
    return d.toLocaleDateString("en-GB");
}

const safeStr = (v) => (v == null ? "" : String(v).trim());
const shorten = (s, n = 16) => {
    const t = safeStr(s);
    if (!t) return "";
    return t.length > n ? `${t.slice(0, n)}…` : t;
};

// chart click helper
const chartClickLabel = (evt, elements, chart) => {
    if (!elements?.length) return null;
    const idx = elements[0]?.index;
    const label = chart?.data?.labels?.[idx];
    return label != null ? String(label) : null;
};

function CustomerDashboard() {
    const [opportunities, setOpportunities] = useState([]);
    const [exchangeRates, setExchangeRates] = useState(null);

    const [targetCurrency, setTargetCurrency] = useState("USD");
    const [loading, setLoading] = useState(true);

    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);

    const ALLOWED_STATUS_LABELS = ["Prospect", "Needs Assessment", "Target"];
    const [selectedStatuses, setSelectedStatuses] = useState(ALLOWED_STATUS_LABELS);

    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [typeFilter, setTypeFilter] = useState(null); // clicked transport type filter

    const navigate = useNavigate();

    // -------------------- Helpers --------------------
    const normalizeStatus = (op) => {
        const raw = op?.status ?? op?.opportunityStatus;
        if (!raw) return "";
        if (typeof raw === "string") return raw.trim();
        if (typeof raw === "object" && raw.label) return String(raw.label).trim();
        return String(raw).trim();
    };

    const convertCurrency = (amount, fromCurrency) => {
        if (!exchangeRates) return amount;
        const baseRate = exchangeRates.rates?.[fromCurrency];
        const targetRate = exchangeRates.rates?.[targetCurrency];
        if (!baseRate || !targetRate) return amount;
        return (amount / baseRate) * targetRate;
    };

    const applyStatusFilter = (data) => {
        if (!selectedStatuses || selectedStatuses.length === 0) return [];
        const set = new Set(selectedStatuses);
        return data.filter((op) => set.has(normalizeStatus(op)));
    };

    const applyDateRangeFilter = (data) => {
        if (!startDate || !endDate) return data;

        const start = new Date(startDate.startOf("day").toISOString());
        const end = new Date(endDate.endOf("day").toISOString());

        return data.filter((op) => {
            const dt = new Date(op.estimatedCloseDate);
            if (isNaN(dt.getTime())) return false;
            return dt >= start && dt <= end;
        });
    };

    const getFilteredOps = (data) => {
        const withStatus = applyStatusFilter(data);
        return applyDateRangeFilter(withStatus);
    };

    // -------------------- Fetch --------------------
    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const [response, rates] = await Promise.all([
                    CrudService.getOpportunities(),
                    fetchExchangeRates(),
                ]);

                const opps = response.data.map((item) => ({ id: item.id, ...item.attributes }));
                setOpportunities(opps);
                setExchangeRates(rates);
            } catch (error) {
                console.error("Data fetching error:", error);
            }
            setLoading(false);
        };

        fetchData();
    }, []);

    // -------------------- Base filtered data --------------------
    const baseOpps = useMemo(() => getFilteredOps(opportunities), [
        opportunities,
        startDate,
        endDate,
        selectedStatuses,
    ]);

    // -------------------- Customer aggregation --------------------
    const customerAgg = useMemo(() => {
        const map = {};
        baseOpps.forEach((op) => {
            const c = safeStr(op.customer) || "NoName";
            map[c] = (map[c] || 0) + 1;
        });

        const sorted = Object.entries(map)
            .map(([label, count]) => ({ label, count }))
            .sort((a, b) => b.count - a.count);

        const fullLabels = sorted.map((x) => x.label);
        const shortLabels = fullLabels.map((l) => shorten(l, 18));
        const counts = sorted.map((x) => x.count);

        return { fullLabels, shortLabels, counts };
    }, [baseOpps]);

    // -------------------- Selected customer ops --------------------
    const selectedCustomerOps = useMemo(() => {
        if (!selectedCustomer) return [];
        return baseOpps.filter((op) => safeStr(op.customer) === safeStr(selectedCustomer));
    }, [baseOpps, selectedCustomer]);

    const selectedCustomerRevenue = useMemo(() => {
        if (!selectedCustomer) return 0;
        return selectedCustomerOps.reduce((acc, op) => {
            return acc + convertCurrency(Number(op.totalRevenue || 0), op.freightRateCurrency);
        }, 0);
    }, [selectedCustomer, selectedCustomerOps, targetCurrency, exchangeRates]);

    // -------------------- KPI (global) --------------------
    const totalCustomers = useMemo(() => new Set(baseOpps.map((x) => safeStr(x.customer) || "NoName")).size, [baseOpps]);
    const totalOpportunities = useMemo(() => baseOpps.length, [baseOpps]);

    // -------------------- Charts --------------------
    const customerBarData = useMemo(() => {
        const bg = customerAgg.fullLabels.map((name) => {
            if (!selectedCustomer) return "rgba(142, 68, 173, 0.85)";
            return name === selectedCustomer ? "rgba(142, 68, 173, 0.95)" : "rgba(142, 68, 173, 0.20)";
        });

        return {
            labels: customerAgg.shortLabels,
            fullLabels: customerAgg.fullLabels,
            datasets: [
                {
                    label: "Opportunities",
                    data: customerAgg.counts,
                    backgroundColor: bg,
                    borderRadius: 12,
                    maxBarThickness: 48,
                },
            ],
        };
    }, [customerAgg, selectedCustomer]);

    const customerBarOptions = useMemo(
        () => ({
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
                legend: { display: false },
                tooltip: {
                    callbacks: {
                        title: (items) => {
                            const idx = items?.[0]?.dataIndex ?? 0;
                            return customerBarData.fullLabels?.[idx] || "";
                        },
                        label: (ctx) => ` ${ctx.raw ?? 0} opportunities`,
                    },
                },
            },
            scales: {
                x: {
                    grid: { display: false },
                    ticks: { maxRotation: 35, minRotation: 35 },
                },
                y: {
                    beginAtZero: true,
                    grid: { color: "rgba(0,0,0,0.06)" },
                    ticks: { stepSize: 1 },
                },
            },
            onClick: (evt, elements, chart) => {
                const idx = elements?.[0]?.index;
                if (idx == null) return;
                const full = customerBarData.fullLabels?.[idx];
                if (!full) return;

                // toggle
                if (selectedCustomer === full) {
                    setSelectedCustomer(null);
                    setTypeFilter(null);
                } else {
                    setSelectedCustomer(full);
                    setTypeFilter(null);
                }
            },
        }),
        [customerBarData, selectedCustomer]
    );

    const typeChartData = useMemo(() => {
        if (!selectedCustomerOps.length) return null;

        const map = {};
        selectedCustomerOps.forEach((op) => {
            const t = safeStr(op.selectedTransportType) || "Undefined";
            map[t] = (map[t] || 0) + 1;
        });

        const labels = Object.keys(map);
        const counts = labels.map((l) => map[l]);

        return {
            labels,
            datasets: [
                {
                    label: "Opportunities",
                    data: counts,
                    backgroundColor: "rgba(75, 192, 192, 0.75)",
                    borderRadius: 12,
                    maxBarThickness: 56,
                },
            ],
        };
    }, [selectedCustomerOps]);

    const typeChartOptions = useMemo(
        () => ({
            responsive: true,
            maintainAspectRatio: false,
            plugins: { legend: { display: false } },
            scales: {
                x: { grid: { display: false } },
                y: { beginAtZero: true, grid: { color: "rgba(0,0,0,0.06)" }, ticks: { stepSize: 1 } },
            },
            onClick: (evt, elements, chart) => {
                const label = chartClickLabel(evt, elements, chart);
                if (!label) return;
                setTypeFilter((prev) => (prev === label ? null : label)); // toggle
            },
        }),
        []
    );

    // -------------------- Table --------------------
    const formatSalesReps = (salesRep) => {
        if (!salesRep) return "-";
        if (Array.isArray(salesRep)) return salesRep.map((rep) => rep.name).join(", ");
        if (typeof salesRep === "object") return salesRep.name || "-";
        return salesRep;
    };

    const tableColumns = [
        { Header: "Customer", accessor: "customer" },
        { Header: "Opp Name", accessor: "opportunityName" },
        { Header: "Estimated Close", accessor: "estimatedCloseDate" },
        { Header: "Transport Type", accessor: "transportType" },
        { Header: "Total Revenue", accessor: "totalRevenue" },
        { Header: "Sales Rep", accessor: "salesRep" },
        { Header: "Status", accessor: "status" },
    ];

    const opsForTable = useMemo(() => {
        if (!selectedCustomer) return [];
        if (!typeFilter) return selectedCustomerOps;
        return selectedCustomerOps.filter((op) => safeStr(op.selectedTransportType) === safeStr(typeFilter));
    }, [selectedCustomer, selectedCustomerOps, typeFilter]);

    const tableRows = useMemo(() => {
        return (opsForTable || []).map((op) => {
            const revenue = convertCurrency(Number(op.totalRevenue || 0), op.freightRateCurrency);

            return {
                customer: (
                    <MuiTooltip title={op.customer || ""} placement="top" arrow>
                        <span>{op.customer?.length > 40 ? `${op.customer.slice(0, 37)}...` : op.customer || ""}</span>
                    </MuiTooltip>
                ),
                opportunityName: (
                    <span
                        style={{ color: "#ff7400", cursor: "pointer", textDecoration: "underline" }}
                        onClick={() => navigate(`/opportunity/create-opportunity/details?id=${op.id}`)}
                    >
            {op.opportunityName || "-"}
          </span>
                ),
                estimatedCloseDate: formatDateDDMMYYYY(op.estimatedCloseDate),
                transportType: op.selectedTransportType || "-",
                totalRevenue: `${fmt2(revenue)} ${targetCurrency}`,
                salesRep: formatSalesReps(op.salesRep),
                status: normalizeStatus(op) || "-",
            };
        });
    }, [opsForTable, targetCurrency, exchangeRates]);

    // -------------------- Clear --------------------
    const isClearEnabled =
        !!selectedCustomer ||
        !!typeFilter ||
        (startDate && endDate) ||
        (selectedStatuses?.length !== ALLOWED_STATUS_LABELS.length) ||
        !ALLOWED_STATUS_LABELS.every((s) => selectedStatuses?.includes(s));

    const clearAll = () => {
        setStartDate(null);
        setEndDate(null);
        setSelectedStatuses(ALLOWED_STATUS_LABELS);
        setSelectedCustomer(null);
        setTypeFilter(null);
    };

    // -------------------- UI --------------------
    if (loading) {
        return (
            <MDBox display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </MDBox>
        );
    }

    return (
        <MDBox pb={3}>
            {/* Header + quick actions */}
            <Box mb={2} display="flex" alignItems="flex-start" justifyContent="space-between" flexWrap="wrap" gap={2}>
                <Box>
                    <MDTypography variant="h5" sx={{ fontWeight: 700 }}>
                        Customer Statistics Dashboard
                    </MDTypography>
                    <MDTypography variant="button" sx={{ color: "text.secondary", fontWeight: 600 }}>
                        Click customer bars to drill down • Click transport bars to filter table
                    </MDTypography>
                </Box>

                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                    {selectedCustomer ? (
                        <Chip
                            label={`Customer: ${selectedCustomer}`}
                            onDelete={() => {
                                setSelectedCustomer(null);
                                setTypeFilter(null);
                            }}
                            sx={{ borderRadius: 2, fontWeight: 700 }}
                        />
                    ) : (
                        <Chip
                            label="No customer selected"
                            sx={{ borderRadius: 2, fontWeight: 700, bgcolor: "rgba(0,0,0,0.04)" }}
                        />
                    )}

                    {typeFilter ? (
                        <Chip
                            color="success"
                            label={`Type: ${typeFilter}`}
                            onDelete={() => setTypeFilter(null)}
                            sx={{ borderRadius: 2, fontWeight: 700 }}
                        />
                    ) : null}

                    <Button
                        variant="contained"
                        onClick={clearAll}
                        disabled={!isClearEnabled}
                        sx={{ borderRadius: 2, textTransform: "none", fontWeight: 700 }}
                    >
                        Clear
                    </Button>
                </Stack>
            </Box>

            {/* KPI row */}
            <Grid container spacing={2} mb={2}>
                <Grid item xs={12} md={3}>
                    <Card sx={{ borderRadius: "10px", boxShadow: "none" }}>
                        <CardContent>
                            <MDTypography variant="button" sx={{ color: "text.secondary", fontWeight: 800 }}>
                                Total Customers
                            </MDTypography>
                            <MDTypography variant="h4" sx={{ fontWeight: 700 }}>
                                {totalCustomers}
                            </MDTypography>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} md={3}>
                    <Card sx={{ borderRadius: "10px", boxShadow: "none" }}>
                        <CardContent>
                            <MDTypography variant="button" sx={{ color: "text.secondary", fontWeight: 800 }}>
                                Total Opportunities
                            </MDTypography>
                            <MDTypography variant="h4" sx={{ fontWeight: 700 }}>
                                {totalOpportunities}
                            </MDTypography>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} md={3}>
                    <Card sx={{ borderRadius: "10px", boxShadow: "none" }}>
                        <CardContent>
                            <MDTypography variant="button" sx={{ color: "text.secondary", fontWeight: 800 }}>
                                Selected Opportunities
                            </MDTypography>
                            <MDTypography variant="h4" sx={{ fontWeight: 700 }}>
                                {selectedCustomer ? selectedCustomerOps.length : "—"}
                            </MDTypography>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} md={3}>
                    <Card sx={{ borderRadius: "10px", boxShadow: "none" }}>
                        <CardContent>
                            <MDTypography variant="button" sx={{ color: "text.secondary", fontWeight: 800 }}>
                                Selected Revenue ({targetCurrency})
                            </MDTypography>
                            <MDTypography variant="h4" sx={{ fontWeight: 700 }}>
                                {selectedCustomer ? fmt2(selectedCustomerRevenue) : "—"}
                            </MDTypography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Filters (rich card) */}
            <Card sx={{ borderRadius: "10px", boxShadow: "none", mb: 3 }}>
                <CardContent>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
                            <TextField
                                select
                                label="Currency"
                                value={targetCurrency}
                                onChange={(e) => setTargetCurrency(e.target.value)}
                                SelectProps={{ native: true }}
                                sx={{ minWidth: 160, "& .MuiInputBase-root": { height: "40px" } }}
                                size="small"
                                variant="outlined"
                            >
                                {["USD", "TRY", "EUR", "GBP"].map((c) => (
                                    <option key={c} value={c}>
                                        {c}
                                    </option>
                                ))}
                            </TextField>

                            <DatePicker
                                label="Start Date"
                                format="DD.MM.YYYY"
                                value={startDate}
                                onChange={(v) => setStartDate(v)}
                                slotProps={{
                                    textField: {
                                        variant: "outlined",
                                        size: "small",
                                        sx: { minWidth: 160, "& .MuiInputBase-root": { height: "40px" } },
                                    },
                                }}
                            />

                            <DatePicker
                                label="End Date"
                                format="DD.MM.YYYY"
                                value={endDate}
                                onChange={(v) => setEndDate(v)}
                                slotProps={{
                                    textField: {
                                        variant: "outlined",
                                        size: "small",
                                        sx: { minWidth: 160, "& .MuiInputBase-root": { height: "40px" } },
                                    },
                                }}
                            />

                            <Autocomplete
                                multiple
                                disableCloseOnSelect
                                options={ALLOWED_STATUS_LABELS}
                                value={selectedStatuses}
                                onChange={(_, v) => setSelectedStatuses(v)}
                                renderOption={({ key: optionKey, ...props }, option, { selected }) => (
                                    <li key={optionKey} {...props}>
                                        <Checkbox checked={selected} sx={{ mr: 1 }} />
                                        {option}
                                    </li>
                                )}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Status"
                                        size="small"
                                        variant="outlined"
                                        sx={{ minWidth: 280, "& .MuiInputBase-root": { minHeight: "40px" } }}
                                    />
                                )}
                            />

                            <Box sx={{ flex: 1 }} />

                            <MDTypography variant="button" sx={{ color: "text.secondary", fontWeight: 700 }}>
                                Showing: {baseOpps.length} opportunities
                            </MDTypography>
                        </Box>
                    </LocalizationProvider>
                </CardContent>
            </Card>

            {/* Main chart + drill down */}
            <Grid container spacing={2}>
                <Grid item xs={12} md={selectedCustomer ? 7 : 12}>
                    <Card sx={{ borderRadius: "10px", boxShadow: "none", height: 520 }}>
                        <CardContent sx={{ height: "100%" }}>
                            <Box mb={1} display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
                                <MDTypography variant="h6" sx={{ fontWeight: 700 }}>
                                    Opportunities by Customer
                                </MDTypography>
                                <MDTypography variant="button" sx={{ color: "text.secondary", fontWeight: 700 }}>
                                    Click to select / unselect
                                </MDTypography>
                            </Box>
                            <Divider sx={{ mb: 2 }} />
                            <Box sx={{ height: 420 }}>
                                <Bar data={customerBarData} options={customerBarOptions} />
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Right side drill-down */}
                {selectedCustomer ? (
                    <Grid item xs={12} md={5}>
                        <Card sx={{ borderRadius: "10px", boxShadow: "none", height: 520 }}>
                            <CardContent sx={{ height: "100%" }}>
                                <Box display="flex" alignItems="center" justifyContent="space-between" mb={1} flexWrap="wrap" gap={1}>
                                    <MDTypography variant="h6" sx={{ fontWeight: 700 }}>
                                        Transport Type Analysis
                                    </MDTypography>
                                    <Chip
                                        size="small"
                                        label={typeFilter ? `Filtered: ${typeFilter}` : "No type filter"}
                                        sx={{ borderRadius: 2, fontWeight: 700, bgcolor: typeFilter ? "rgba(46, 125, 50, 0.12)" : "rgba(0,0,0,0.04)" }}
                                    />
                                </Box>
                                <Divider sx={{ mb: 2 }} />

                                <Box sx={{ height: 380 }}>
                                    {typeChartData ? <Bar data={typeChartData} options={typeChartOptions} /> : null}
                                </Box>

                                <MDTypography variant="button" sx={{ color: "text.secondary", fontWeight: 700, mt: 1, display: "block" }}>
                                    Tip: Click a bar to filter table • Click again to remove
                                </MDTypography>
                            </CardContent>
                        </Card>
                    </Grid>
                ) : null}
            </Grid>

            {/* Table */}
            {selectedCustomer ? (
                <MDBox mt={3}>
                    <Card sx={{ borderRadius: "10px", boxShadow: "none" }}>
                        <CardContent>
                            <Box mb={1} display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
                                <Box>
                                    <MDTypography variant="h6" sx={{ fontWeight: 700 }}>
                                        Opportunities for {selectedCustomer}
                                    </MDTypography>
                                    <MDTypography variant="button" sx={{ color: "text.secondary", fontWeight: 700 }}>
                                        {typeFilter ? `Type filter: ${typeFilter}` : "All types"}
                                    </MDTypography>
                                </Box>

                                <Stack direction="row" spacing={1} alignItems="center">
                                    <Chip
                                        size="small"
                                        label={`Rows: ${tableRows.length}`}
                                        sx={{ borderRadius: 2, fontWeight: 700 }}
                                    />
                                    {typeFilter ? (
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            color="error"
                                            onClick={() => setTypeFilter(null)}
                                            sx={{
                                                borderRadius: 2,
                                                textTransform: "none",
                                                fontWeight: 700,
                                                borderWidth: 2,
                                                bgcolor: "rgb(255,116,0)",
                                                color: "text.secondary",
                                            }}
                                        >
                                            Clear Type Filter
                                        </Button>

                                    ) : null}
                                </Stack>
                            </Box>

                            <DataTable
                                table={{ columns: tableColumns, rows: tableRows }}
                                canSearch
                                entriesPerPage={5}
                                showTotalEntries
                                isSorted
                            />
                        </CardContent>
                    </Card>
                </MDBox>
            ) : null}
        </MDBox>
    );
}

export default CustomerDashboard;
